import { type ReactNode } from 'react';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { type HomeAudience } from '@backrs_ui/grow-ui/src/pages/Home/homeAudience';
import { HomeAnalyticsContent } from '@backrs_ui/grow-ui/src/pages/Home/molecules/HomeAnalyticsContent';
import { HomeTabContent } from '@backrs_ui/grow-ui/src/pages/Home/molecules/HomeTabContent';

import { type BoxType } from './InfoBox.config';

type HomeContent = Partial<Record<BoxType, ReactNode>>;

const STUDENT_HOME_CONTENT: HomeContent = {
  FEATURED: <HomeTabContent key="FEATURED" tab="FEATURED" />,
  TOOLS: <HomeTabContent key="TOOLS" tab="TOOLS" />,
  COACHES: <HomeTabContent key="COACHES" tab="COACHES" />,
};

const HOME_CONTENT_BY_AUDIENCE: Record<HomeAudience, HomeContent> = {
  STUDENT: STUDENT_HOME_CONTENT,
  COACH: {
    ...STUDENT_HOME_CONTENT,
    FEATURED: <HomeAnalyticsContent key="FEATURED" />,
  },
  INSTITUTION_ADMIN: {
    ...STUDENT_HOME_CONTENT,
    FEATURED: <HomeAnalyticsContent key="FEATURED" />,
  },
};

type InfoBoxEmptyStateInput = Readonly<{
  boxType: BoxType | undefined;
  audience: HomeAudience | null;
  pathname: string;
  inSubsession: boolean;
  inStructuredSession: boolean;
}>;

export const getInfoBoxEmptyState = ({
  boxType,
  audience,
  pathname,
  inSubsession,
  inStructuredSession,
}: InfoBoxEmptyStateInput): ReactNode | undefined => {
  if (!boxType) return undefined;
  // in a structured session, suppress the home empty-state; the output renders elsewhere
  if (inStructuredSession) return undefined;
  if (pathname !== APP_ROUTE.HOME) return undefined;
  // during a subsession only FEATURED keeps its home content; other tabs stay empty
  if (inSubsession && boxType !== 'FEATURED') return undefined;
  // Only FEATURED differs by audience, so the rest render without waiting on permissions.
  if (boxType === 'FEATURED' && !audience) return undefined;

  return HOME_CONTENT_BY_AUDIENCE[audience ?? 'STUDENT'][boxType];
};
